'use client';

/**
 * SwimlaneCard — a reusable lane card: a titled group of child cards that
 * defaults to ONE ROW and expands to the full set.
 *
 * The generic form of the intent swimlane, decoupled from intent so any
 * grouped view reuses it (vendor shelves today; category lanes / collection
 * rows tomorrow). The caller passes a title, a full count, an optional leading
 * mark, and the child cards. Collapsed = the first row (`perRow`); expanded =
 * every child. No horizontal scroll — expand reveals the rest as a wrapped
 * wall through the shared CardGrid. A card (registered/exported), not chrome.
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain canvas
 */

import { Children, useMemo, type ReactNode } from 'react';
import { ChevronsDown, ChevronsUp } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import { CardGrid } from './card-grid';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

export function SwimlaneCard({
  title,
  count,
  leading,
  laneId,
  contentKey,
  expanded,
  onExpandToggle,
  expandLabel,
  collapseLabel,
  perRow = 6,
  children,
}: {
  title: string;
  count: number; // full count for the header (may exceed shipped items)
  leading?: ReactNode; // e.g. a vendor monogram
  laneId: string;
  contentKey: string;
  expanded: boolean;
  onExpandToggle: () => void;
  expandLabel: string; // "Show all {n}" — chrome from the BFF label lane
  collapseLabel: string;
  perRow?: number; // one collapsed row = this many (matches CardGrid's columns)
  children: ReactNode;
}) {
  const all = Children.toArray(children);
  const shown = expanded ? all : all.slice(0, perRow);
  const hasMore = all.length > perRow;

  useCardExport(
    useMemo(() => ({
      label: title,
      content_key: contentKey,
      surface: 'canvas' as const,
      status: 'loaded' as const,
      query_key: null,
      text: `${title} · ${count}`,
      json: { lane_id: laneId, count, showing: shown.length, expanded },
    }), [title, contentKey, laneId, count, shown.length, expanded]),
  );

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        {leading}
        <h3 className={cn(text.heading, 'truncate')}>{title}</h3>
        {hasMore && (
          <button
            type="button"
            onClick={onExpandToggle}
            className={cn(text.muted, 'ml-auto flex items-center gap-1 hover:text-foreground transition-colors')}
          >
            {expanded ? (
              <><ChevronsUp className="h-3.5 w-3.5" /> {collapseLabel}</>
            ) : (
              <><ChevronsDown className="h-3.5 w-3.5" /> {expandLabel.replace('{n}', String(all.length))}</>
            )}
          </button>
        )}
      </div>
      <CardGrid>{shown}</CardGrid>
    </section>
  );
}
